import { useAnalytics } from '#~/analytics/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { createSettingsLifecycleTracker } from '#~/analytics/domains/index.js';
import { Section } from '#~/room/components/common/section/index.js';
import { Controls } from '#~/room/components/image/settings/controls.jsx';
import {
  useAdvancedSettingsForSectionGroup,
  useSortedSectionsForSectionGroupKey
} from '#~/room/components/image/settings/hooks.js';
import { sortWeightedItems, useWiring } from '#~/room/components/image/settings/wiring.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { Device } from '@studio/models/device';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type {
  Control,
  DeviceRegistryInterfaceSection
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { DeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';
import type {
  DeviceRegistryInterfaceItem,
  DeviceRegistryInterfaceSectionType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { Columns, HRule } from '../layout.jsx';
import { Preview } from '../preview.jsx';
import { useProducer, useStore } from '../store/hooks.js';
import {
  getCurrentSectionForPanel,
  initializeSectionStateForPanel,
  updateSectionForPanel
} from '../utils/section-state-utils.js';
import { SectionDescription } from './section-description.js';
import { sectionHasVisibleItems, selectVisibleSectionLabels } from './section-visibility.js';
import { SectionsContainer } from './sections-container.js';

interface GenericSettingsSectionProps extends HtmlAttributes {
  sectionGroupKey: string;
  previewTitle: string;
}

/** Generic settings section component that can handle any section type using DeviceSettingsControls */
export const GenericSettingsSection = ({
  sectionGroupKey,
  previewTitle,
  ...props
}: GenericSettingsSectionProps): ReactElement => {
  const [candidate, panel, overrides, manufacturer] = useStore(
    (µ) => [µ.candidate, µ.panel, µ.overrides, µ.manufacturer],
    shallow
  );
  const storeState = useStore((µ) => µ, shallow);
  const { conditionsMap, interfaceSections, commandMap } = useWiring(candidate.id, overrides);

  // Get current section using dynamic utility
  const section = getCurrentSectionForPanel(storeState, panel);
  const producer = useProducer();
  const { track } = useAnalytics();
  const settingsLifecycleTracker = useMemo(() => createSettingsLifecycleTracker(track), [track]);
  const selectedModel = manufacturer?.config?.model as
    | {
        id?: string | number;
        name?: string;
        brandId?: string | number;
        brandName?: string;
        brand?: { name?: string };
      }
    | undefined;
  const deviceAnalyticsCatalogContext = useMemo(
    () => resolveDeviceAnalyticsCatalogContextFromModel(selectedModel, candidate.modelId),
    [candidate.modelId, selectedModel]
  );

  // Get sorted sections for this section group
  const sortedSections = useSortedSectionsForSectionGroupKey(candidate.id, sectionGroupKey);

  // Get advanced settings for this section group to determine which section types to render
  const advancedSettings = useAdvancedSettingsForSectionGroup(candidate.id, sectionGroupKey);

  // Filter sorted sections to only include those with visible items. Shared with
  // the sub-section strip (see section-visibility.ts) so the strip and this
  // panel cannot disagree about whether the group is empty.
  const visibleSections = useMemo(
    () => selectVisibleSectionLabels(sortedSections, interfaceSections, conditionsMap),
    [sortedSections, interfaceSections, conditionsMap]
  );

  // Get the section type for the currently selected section
  const currentSectionType = useMemo(() => {
    if (!section) return null;

    // Find the interface section that matches the currently selected section
    const matchingSection = advancedSettings.find((setting) => setting.label === section);
    return matchingSection?.type || null;
  }, [advancedSettings, section]);

  // Auto-select first available section (considering conditionMap)
  useEffect(() => {
    if (visibleSections.length > 0) {
      const firstVisibleSection = visibleSections[0];
      if (firstVisibleSection && (!visibleSections.includes(section) || !section)) {
        producer((draft) => {
          // Initialize section state for dynamic panels
          initializeSectionStateForPanel(draft, panel);
          // Update section using dynamic utility
          updateSectionForPanel(draft, panel, firstVisibleSection);
        });
      }
    }
  }, [visibleSections, section, producer, sectionGroupKey, panel]);

  const handleSectionChange = (selection: string): void => {
    producer((draft) => {
      // Initialize section state for dynamic panels
      initializeSectionStateForPanel(draft, panel);
      // Update section using dynamic utility
      updateSectionForPanel(draft, panel, selection);
    });
  };

  const addMutation = (mutation: DeepPartial<Control>): void => {
    producer((draft) => {
      Object.entries(mutation.device?.[candidate.id]?.deviceSettings ?? {}).forEach(
        ([key, value]) => {
          if (!value) return;

          draft.overrides[key] = value;
          const commandDefinition = commandMap[Number(key)] as Record<string, unknown> | undefined;
          const commandLabel =
            (commandDefinition?.name as string | undefined) ??
            (commandDefinition?.label as string | undefined) ??
            (commandDefinition?.description as string | undefined) ??
            key;
          settingsLifecycleTracker.trackDeviceSettingsChange({
            command_key: key,
            command: String(commandLabel),
            value,
            section: section ?? sectionGroupKey,
            section_group: sectionGroupKey,
            surface: 'generic_settings_section',
            device_id: candidate.id,
            ...deviceAnalyticsCatalogContext
          });
        }
      );

      draft.flags.isDeviceSettingsDirty = Object.keys(draft.overrides).length > 0;
      draft.flags.isDirty = draft.flags.isDeviceSettingsDirty || draft.flags.isVideoSettingsDirty;
    });
  };

  return (
    <div {...props}>
      {/* The section group is passed explicitly: the strip cannot derive it from
          the panel when the two differ (Panel.Control lists the 'Controls' group).
          The strip owns the whole-tab empty state ("No settings available"): every
          tab that mounts this component is registry-only, so an empty group IS an
          empty tab and the message is the right one. */}
      <SectionsContainer onSectionChange={handleSectionChange} sectionGroupKey={sectionGroupKey} />
      <SectionDescription section={section} />
      <HRule className='mt-[1rem]' />

      <Columns>
        <Columns.Column>
          {/* Render controls only for the currently selected section */}
          {currentSectionType && section && (
            <SelectedSectionControls
              deviceId={candidate.id}
              mutationOverrides={overrides}
              onMutation={addMutation}
              sectionType={currentSectionType}
              selectedSectionLabel={section}
              variant='tray'
            />
          )}
        </Columns.Column>

        <Columns.Column>
          <h3>{previewTitle}</h3>
          <Preview />
        </Columns.Column>
      </Columns>
    </div>
  );
};

/**
 * Component that renders controls for only the selected section
 * This is a custom version of DeviceSettingsControls that filters by both sectionType and section label
 */
interface SelectedSectionControlsProps {
  deviceId: Device['id'];
  mutationOverrides?: Record<string, DeepPartial<DeviceSetting>>;
  onMutation?: (mutation: DeepPartial<Control>) => void;
  sectionType: DeviceRegistryInterfaceSectionType;
  selectedSectionLabel: string;
  variant?: 'inline' | 'sidebar' | 'tray';
}

/**
 * @internal
 *
 * Component that renders controls for only the selected section
 * This is a custom version of `DeviceSettingsControls` that filters by both sectionType and section label
 */
const SelectedSectionControls = ({
  deviceId,
  mutationOverrides,
  onMutation,
  sectionType,
  selectedSectionLabel,
  variant = 'tray'
}: SelectedSectionControlsProps): ReactElement => {
  const {
    agent,
    commandMap,
    isOffline,
    conditionsMap,
    defaultsMap,
    deviceSettings,
    interfaceSections
  } = useWiring(deviceId, mutationOverrides);

  const convertSectionItems = (section: DeviceRegistryInterfaceSection) => {
    const convertedItems: Record<string, DeviceRegistryInterfaceItem> = {};
    Object.entries(section.items).forEach(([key, item]) => {
      convertedItems[key] = item as any;
    });
    return convertedItems;
  };

  return (
    <div>
      {Object.entries(interfaceSections ?? {})
        .filter(
          ([_, section]) => section.type === sectionType && section.label === selectedSectionLabel
        )
        .sort(sortWeightedItems)
        .filter(([_, section]) => sectionHasVisibleItems(section, conditionsMap))
        .map(([id, section]) => (
          <Section
            className={twMerge((variant === 'tray' || variant === 'inline') && 'px-0')}
            key={id}
            slug={section.label ?? ''}
            variant={variant}
          >
            <div className='space-y-[16px]'>
              <Controls
                agent={agent}
                commandMap={commandMap}
                isOffline={isOffline}
                conditionsMap={conditionsMap}
                defaultsMap={defaultsMap}
                deviceId={deviceId}
                deviceSettings={deviceSettings}
                items={convertSectionItems(section)}
                onMutation={onMutation}
              />
            </div>
          </Section>
        ))}
    </div>
  );
};
